import { useEffect, useEffectEvent } from 'react';
import { useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';

import { rutaFicha } from '#components/mobile/budget/useFichaCategoria';
import { usePagadas } from '#components/mobile/budget/usePagadas';
import {
  avisosCandidatos,
  claveAviso,
} from '#components/mobile/pagosGavi/consejos';
import { useFormatosGavi } from '#components/mobile/pagosGavi/formatos';
import { textoConsejo } from '#components/mobile/pagosGavi/textosConsejo';
import { usePagosGavi } from '#components/mobile/pagosGavi/usePagosGavi';
import { usePruebaMovil } from '#components/mobile/plan/usePruebaMovil';
import { useAvisoPareja } from '#components/pareja/useAvisoPareja';
import { useTripulacion } from '#components/pareja/useTripulacion';
import { useMetadataPref } from '#hooks/useMetadataPref';

import { textosAvisos, textosDiario } from './avisos';
import type { AvisoPagoFijo } from './avisos';
import {
  avisarAlSalir,
  escribirTextos,
  EVENTO_RECUENTO,
  leerRecuento,
  sincronizarAvisos,
} from './avisosPush';
import { useAvisos } from './useAvisos';

/**
 * Mantiene al día los toques de este móvil en el servidor y
 * los textos que leerá el service worker; al salir de la app con algo
 * pendiente, pide el toque de «lo que quedó pendiente».
 */
export function useAvisosSegundoPlano() {
  const { t, i18n } = useTranslation();
  const { avisos } = useAvisos();
  // Pareja: el presupuesto abierto, si es compartido, para su aviso.
  useAvisoPareja();
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const compartido = useTripulacion().length > 1;
  const pareja = compartido && cloudFileId ? [cloudFileId] : [];
  const clave = JSON.stringify([avisos, pareja]);
  // Gavi en Pagos fijos (Altamar): los avisos que esperan, de más a menos
  // urgente.
  const mes = monthUtils.currentMonth();
  const { fijos } = usePagadas(mes);
  const gavi = usePagosGavi(fijos);
  const formatos = useFormatosGavi();
  const avisosPagosFijos: AvisoPagoFijo[] = avisosCandidatos(
    [...gavi.consejos.values()]
      .flat()
      .sort((a, b) => a.prioridad - b.prioridad),
  ).map(c => {
    const txt = textoConsejo(t, formatos, c);
    return {
      clave: claveAviso(c, gavi.estado),
      texto: [txt.titulo, txt.texto],
      ruta: rutaFicha(c.categoria, mes),
    };
  });
  const clavePagosFijos = avisosPagosFijos.map(a => a.clave).join(',');
  // Prueba gratis: los avisos del día 30, 43 y el último, con lo que tiene
  // Rumbo ahora.
  const prueba = usePruebaMovil();

  const textos = async () => {
    // El aviso del mes sale del día 1 al 3: este mes si aún estamos a
    // tiempo; si no, el siguiente.
    const hoy = monthUtils.currentDay();
    const mesQueAvisa =
      Number(hoy.slice(8, 10)) <= 3
        ? monthUtils.currentMonth()
        : monthUtils.nextMonth(monthUtils.currentMonth());
    const nombreMesCerrado = new Date(
      `${monthUtils.prevMonth(mesQueAvisa)}-15T12:00:00`,
    ).toLocaleDateString(i18n.language, { month: 'long' });
    return textosAvisos(
      t,
      avisos,
      leerRecuento(),
      ms =>
        new Date(ms).toLocaleString(i18n.language, {
          weekday: 'long',
          hour: '2-digit',
          minute: '2-digit',
        }),
      textosDiario(
        t,
        avisos.tono,
        mesQueAvisa,
        nombreMesCerrado.charAt(0).toUpperCase() + nombreMesCerrado.slice(1),
      ),
      avisosPagosFijos,
      await prueba.calcular(avisos).catch(() => []),
    );
  };

  const sincronizar = useEffectEvent(() => {
    void textos().then(x => sincronizarAvisos(avisos, x, pareja));
  });
  const alCambiarRecuento = useEffectEvent(() => {
    void textos().then(escribirTextos);
  });
  const alCambiarVisibilidad = useEffectEvent(() => {
    const oculta = document.visibilityState === 'hidden';
    avisarAlSalir(avisos, oculta);
    // Al salir, los textos con lo último (lo apartado en Rumbo, por ejemplo).
    if (oculta) {
      alCambiarRecuento();
    }
  });

  useEffect(() => sincronizar(), [clave, i18n.language]);
  useEffect(() => alCambiarRecuento(), [clavePagosFijos, prueba.clave]);

  useEffect(() => {
    const recuento = () => alCambiarRecuento();
    const visibilidad = () => alCambiarVisibilidad();
    window.addEventListener(EVENTO_RECUENTO, recuento);
    document.addEventListener('visibilitychange', visibilidad);
    return () => {
      window.removeEventListener(EVENTO_RECUENTO, recuento);
      document.removeEventListener('visibilitychange', visibilidad);
    };
  }, []);
}
